import {
    Box,
    Card,
    CardBody,
    Flex,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { snakeCase } from 'lodash';
import { useMemo } from 'react';
import { BiCalculator, BiMenu, BiMinus, BiPlus } from 'react-icons/bi';
import { RiBook2Line, RiStackLine } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { Position, PositionType, ResourceType } from '../_openapi/portfolio-optimizer';
import { usePositions } from '../api/position';
import { AssetDrawer, useAssetDrawerControls } from '../Asset/AssetDrawer/AssetDrawer';
import { ManualTrade } from '../TradeBlotter/TradeBlotter';
import { useTradeBlotterContext } from '../TradeBlotter/TradeBlotterContext';
import { TradeBudgetModal, useTradeBudgetModalControls } from '../TradeBlotter/TradeBudgetModal';
import { tradeCalculator } from '../TradeBlotter/tradeCalculator';
import { DataVariantSelector } from '../widgets/DataVariantSelector';
import { PageHeader } from '../widgets/PageHeader';
import { AsOfDateTag } from '../widgets/Table/AsOfDateTag';
import { CreateWidgetFromTable } from '../widgets/Table/CreateWidgetFromTable';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';
import { useTableColumnSettings } from '../widgets/Table/useTableColumnSettings';
import { positionListColumns } from './positionListColumns';

const columnHelper = createColumnHelper<Position>();

export const PositionList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { viewAsset, ...assetDrawerProps } = useAssetDrawerControls();
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();
    const { calculateTrade, ...tradeBudgetModalProps } = useTradeBudgetModalControls();
    const { addTrade } = useTradeBlotterContext();

    const { pagination, setPagination, sorting, setSorting, resetTable, columnFilters, setColumnFilters } =
        usePersistentTableState(positionListColumns);

    const dataVariant = (searchParams.get('dataVariant') as DataVariant) || 'report';
    const { data: positionsPage, isFetching } = usePositions({
        pagination,
        columnFilters,
        sorting,
        dataVariant,
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => {
                    return (
                        <Menu isLazy>
                            <MenuButton
                                size="xs"
                                as={IconButton}
                                aria-label="Options"
                                icon={<Icon boxSize={3} as={BiMenu} />}
                                w="20px"
                                minW="20px"
                                h="20px"
                                variant="outline"
                                borderRadius="sm"
                            />
                            <Portal>
                                <MenuList>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiBook2Line} />}
                                        onClick={() => viewPortfolio(row.original.base.pId)}
                                    >
                                        Portfolio details
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiStackLine} />}
                                        onClick={() => viewAsset(row.original.base.sId)}
                                    >
                                        Security details
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiMinus} />}
                                        onClick={() => {
                                            addTrade({
                                                portfolio: {
                                                    portfolioGuid: row.original.base.portfolioGuid,
                                                },
                                                asset: {
                                                    sId: row.original.base.sId,
                                                },
                                                side: 'SELL',
                                                quantity:
                                                    row.original.base.quantity /
                                                    (row.original.base.contractMultiplier || 1),
                                                orderType: 'LMT',
                                                price: {
                                                    type: tradeCalculator.getPriceTypeFromTradeSide('SELL'),
                                                },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Sell Position
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiPlus} />}
                                        onClick={() => {
                                            addTrade({
                                                portfolio: {
                                                    portfolioGuid: row.original.base.portfolioGuid,
                                                },
                                                asset: {
                                                    sId: row.original.base.sId,
                                                },
                                                side: 'BUY',
                                                quantity: 0,
                                                orderType: 'LMT',
                                                price: {
                                                    type: tradeCalculator.getPriceTypeFromTradeSide('BUY'),
                                                },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Buy Position
                                    </MenuItem>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={BiCalculator} />}
                                        onClick={() => {
                                            calculateTrade({
                                                portfolio: { pId: row.original.base.pId },
                                                asset: { sId: row.original.base.sId },
                                            } as ManualTrade);
                                        }}
                                    >
                                        Calculate Trade
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>
                    );
                },
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...positionListColumns,
        ],
        [addTrade, calculateTrade, viewAsset, viewPortfolio],
    );

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } = useTableColumnSettings('positions');
    const table = useReactTable({
        data: positionsPage?.positions || [],
        columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <PageHeader title="Positions">
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                    <DataVariantSelector
                        currentDataVariant={dataVariant}
                        onClick={(variant) => {
                            setSearchParams((params) => {
                                params.set('dataVariant', variant);
                                return params;
                            });
                        }}
                    />
                    <AsOfDateTag dateString={positionsPage?.asOfDate} />
                </Flex>
            </PageHeader>
            <Card>
                <CardBody>
                    <TableControls
                        table={table}
                        onReset={resetTable}
                        resourceType={ResourceType.POSITION}
                        metaData={{ positionType: snakeCase(dataVariant) as PositionType }}
                    >
                        <Text borderRadius="md" borderWidth={1} py={1} px={4} fontSize="sm" color="whiteAlpha.600">
                            Placeholder for grouping controls
                        </Text>
                        <Box ml="auto">
                            <CreateWidgetFromTable
                                initialValue={{
                                    type: 'position',
                                    queryMeta: {
                                        dataVariant,
                                    },
                                    filters: table.getState().columnFilters,
                                    sort: table.getState().sorting,
                                }}
                            />
                        </Box>
                    </TableControls>
                    <Table table={table} isLoading={isFetching} allowXOverflow />
                    <Pagination table={table} totalItems={positionsPage?.totalNr} />
                </CardBody>
            </Card>
            <PortfolioDrawer {...portfolioDrawerProps} />
            <AssetDrawer {...assetDrawerProps} />
            <TradeBudgetModal {...tradeBudgetModalProps} />
        </>
    );
};
